import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  HiOutlineSearch,
  HiPlus,
  HiChevronRight,
  HiOutlineTruck,
  HiOutlineCollection,
  HiOutlineCalendar,
  HiOutlineRefresh,
  HiOutlineExclamationCircle,
  HiOutlineX,
} from "react-icons/hi";
import { LogTrackingService } from "../../../../services/LogTrackingService";
import { LogService } from "../../../../services/LogService";
import ClientSummary from "./ClientSummary";
import {
  StatTile,
  SkeletonRows,
  EmptyState,
  PageTopBar,
  primaryButtonClass,
  toolbarInputClass,
} from "../shared/ui";

const rowGrid =
  "md:grid-cols-[6.5rem_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_1rem]";

const clientKeyOf = (t) => (t.clientId ? `id:${t.clientId}` : `name:${(t.client || "").trim().toLowerCase()}`);

const LogTrackings = () => {
  const navigate = useNavigate();
  const [trackings, setTrackings] = useState([]);
  const [fellCodeById, setFellCodeById] = useState({});
  const [status, setStatus] = useState("loading");
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  const load = () => {
    setStatus("loading");
    Promise.all([LogTrackingService.GetAllLogTrackings(), LogService.GetAllTreeLogs()]).then(
      ([trackingList, fells]) => {
        const lookup = {};
        (fells || []).forEach((f) => {
          lookup[f.treeFellId] = f.treeFellCode;
        });
        setFellCodeById(lookup);
        if (trackingList === null) {
          setStatus("error");
          return;
        }
        setTrackings(trackingList);
        setStatus("ready");
      }
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(
    () =>
      trackings.map((t) => {
        return {
          ...t,
          logCode: t.treeFellCode || fellCodeById[t.treeFellId] || `#${t.treeFellId}`,
          clientKey: clientKeyOf(t),
        };
      }),
    [trackings, fellCodeById]
  );

  const clients = useMemo(() => {
    const byKey = new Map();
    rows.forEach((r) => {
      if (r.client && !byKey.has(r.clientKey)) byKey.set(r.clientKey, { key: r.clientKey, label: r.client, clientId: r.clientId || 0 });
    });
    const list = [...byKey.values()];
    const accountNames = new Set(list.filter((c) => c.clientId).map((c) => c.label.trim().toLowerCase()));
    return list
      .map((c) =>
        !c.clientId && accountNames.has(c.label.trim().toLowerCase())
          ? { ...c, label: `${c.label} (older records)` }
          : c
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (clientFilter && r.clientKey !== clientFilter) return false;
      if (!q) return true;
      return [r.waybillNumber, r.truckNumber, r.dispatchedBy, r.client, r.logCode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, search, clientFilter]);

  const stats = useMemo(() => {
    const startOfMonth = moment().startOf("month");
    return {
      total: rows.length,
      logs: new Set(rows.map((r) => r.treeFellId)).size,
      thisMonth: rows.filter((r) => moment(r.trackingDate).isSameOrAfter(startOfMonth)).length,
    };
  }, [rows]);

  const hasFilters = search !== "" || clientFilter !== "";

  // Show a client's summary when the filter picks one, or the search narrows the list to one client.
  const summaryClient = useMemo(() => {
    let key = clientFilter;
    if (!key && search.trim() && filtered.length > 0) {
      const keys = new Set(filtered.map((r) => r.clientKey));
      if (keys.size === 1) key = [...keys][0];
    }
    return key ? clients.find((c) => c.key === key) || null : null;
  }, [clientFilter, search, filtered, clients]);
  const summaryTrackings = useMemo(
    () => (summaryClient ? rows.filter((r) => r.clientKey === summaryClient.key) : []),
    [summaryClient, rows]
  );
  const addButton = (
    <button type="button" onClick={() => navigate("add")} className={primaryButtonClass}>
      <HiPlus className="w-4 h-4" />
      Track a log
    </button>
  );

  return (
    <div className="w-full pb-10 space-y-6">
      <PageTopBar />

      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-tclPrimary">Log Management</p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-800">Log Tracking</h2>
          <p className="mt-1 text-sm text-slate-500">
            Record which log left on which truck, under which waybill, and for which client.
          </p>
        </div>
        {addButton}
      </header>

      <section className="grid grid-cols-3 gap-3 sm:gap-4">
        <StatTile
          icon={HiOutlineTruck}
          label="Dispatches"
          value={status === "ready" ? stats.total : "–"}
          tone="bg-sky-50 text-sky-600"
        />
        <StatTile
          icon={HiOutlineCollection}
          label="Logs tracked"
          value={status === "ready" ? stats.logs : "–"}
          tone="bg-tclPrimary/10 text-tclPrimary"
        />
        <StatTile
          icon={HiOutlineCalendar}
          label="This month"
          value={status === "ready" ? stats.thisMonth : "–"}
          tone="bg-amber-50 text-amber-600"
        />
      </section>

      {status === "ready" && summaryClient && (
        <ClientSummary
          key={summaryClient.key}
          label={summaryClient.label}
          clientId={summaryClient.clientId}
          trackings={summaryTrackings}
        />
      )}

      <section className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200/80">
        <div className="flex flex-col gap-3 p-4 border-b border-slate-100 md:flex-row md:items-center">
          <label className="relative flex-1">
            <span className="sr-only">Search log tracking</span>
            <HiOutlineSearch className="absolute w-5 h-5 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search waybill, truck, client, dispatcher or log code"
              className={`w-full pl-10 pr-3 ${toolbarInputClass}`}
            />
          </label>
          <div className="flex items-center gap-2">
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              aria-label="Filter by client"
              className={`pl-3 pr-8 max-w-[14rem] ${toolbarInputClass}`}
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setClientFilter("");
                }}
                className="inline-flex items-center gap-1 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <HiOutlineX className="w-4 h-4" />
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={load}
              title="Refresh"
              aria-label="Refresh"
              className="grid w-10 h-10 transition border place-items-center rounded-xl border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            >
              <HiOutlineRefresh className={`h-4 w-4 ${status === "loading" ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {status === "loading" && <SkeletonRows />}

        {status === "error" && (
          <EmptyState
            icon={HiOutlineExclamationCircle}
            title="Couldn't load log tracking"
            body="The tracking records could not be fetched. Check your connection and try again."
            action={
              <button
                type="button"
                onClick={load}
                className="px-4 py-2 text-sm font-semibold transition border rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Try again
              </button>
            }
          />
        )}

        {status === "ready" && rows.length === 0 && (
          <EmptyState
            icon={HiOutlineTruck}
            title="No logs tracked yet"
            body="When a log is dispatched, record the truck, waybill and client here so every log can be traced."
            action={addButton}
          />
        )}

        {status === "ready" && rows.length > 0 && filtered.length === 0 && (
          <EmptyState
            icon={HiOutlineSearch}
            title="No matching records"
            body="Nothing matches your search or client filter."
          />
        )}

        {status === "ready" && filtered.length > 0 && (
          <>
            <div
              className={`hidden gap-4 px-5 py-3 text-xs font-semibold tracking-wide uppercase md:grid ${rowGrid} bg-slate-50/70 text-slate-500`}
            >
              <span>Date</span>
              <span>Log</span>
              <span>Waybill no</span>
              <span>Truck no</span>
              <span>Dispatched by</span>
              <span>Client</span>
              <span />
            </div>
            <ul className="divide-y divide-slate-100">
              {filtered.map((t) => (
                <li key={t.logTrackingId}>
                  <button
                    type="button"
                    onClick={() => navigate(`${t.logTrackingId}`)}
                    className={`group grid w-full grid-cols-2 gap-x-4 gap-y-2 px-5 py-4 text-left text-sm transition md:items-center md:gap-4 ${rowGrid} hover:bg-tclPrimary/[0.03] focus:outline-none focus-visible:bg-tclPrimary/5`}
                  >
                    <span className="font-medium text-slate-700 tabular-nums">
                      {moment(t.trackingDate).format("DD MMM YYYY")}
                    </span>
                    <span className="flex justify-end md:justify-start">
                      <span className="rounded-md bg-tclPrimary/10 px-1.5 py-0.5 font-mono text-xs font-semibold text-tclPrimary">
                        {t.logCode}
                      </span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-slate-400 md:hidden">Waybill no</span>
                      <span className="block font-mono truncate text-slate-800">{t.waybillNumber}</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-slate-400 md:hidden">Truck no</span>
                      <span className="block font-mono truncate text-slate-800">{t.truckNumber}</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-slate-400 md:hidden">Dispatched by</span>
                      <span className="block truncate text-slate-600">{t.dispatchedBy}</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs text-slate-400 md:hidden">Client</span>
                      <span className="block truncate text-slate-600">{t.client}</span>
                    </span>
                    <HiChevronRight className="hidden w-4 h-4 transition md:block text-slate-300 group-hover:translate-x-0.5 group-hover:text-tclPrimary" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-5 py-3 text-xs border-t border-slate-100 text-slate-400">
              Showing {filtered.length} of {rows.length} record{rows.length === 1 ? "" : "s"}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default LogTrackings;
